import { config } from '@/config';
import { NavLink, useNavigate } from 'react-router-dom';
import './index.scss';
import { useUserNavigation } from '@/hooks/useUserNavigation';
import { Button, Drawer, Flex, Grid, Menu, Typography } from 'antd';
import { Fragment } from 'react/jsx-runtime';
import { MenuProps } from 'antd/lib';
import { FC, useState } from 'react';
import { IconsType, SvgIcon } from '@/components/icon';
import { Logo } from '@/components/logo';
import { CloseOutlined } from '@ant-design/icons';
import { useApp } from '@/components/app';
import { GeneralUser, UserRole } from '@/types';
import { CustomButton } from '@/components/ui';
import { ROUTES } from '@/types/routes';
import { isSafeExternalUrl, openExternal } from '@/helpers/openExternal';

const { Text } = Typography;

interface Props {
  open?: boolean;
  onClose?: () => void;
  user?: Partial<GeneralUser>;
  onSupportClick?: () => void;
  onLogoutClick?: () => void;
  isAuthenticated?: boolean;
}

export const Navigation: FC<Props> = ({
  open,
  onClose,
  user,
  onSupportClick,
  onLogoutClick,
  isAuthenticated,
}) => {
  const { lg } = Grid.useBreakpoint();
  const navigation = useUserNavigation();
  const { unreadCount } = useApp().app;
  const [isProfileVisible, setProfileVisible] = useState(false);
  const navigate = useNavigate();

  const userId =
    user?.role === UserRole.CONSULTANT ? user?._id : user?.franchise;

  const handleEditProfile = () => {
    navigate(ROUTES.MY_PROFILE);
    setProfileVisible(false);
    onClose?.();
  };

  const handleSupport = () => {
    onSupportClick?.();
  };

  const handleLogout = () => {
    onLogoutClick?.();
  };

  const handleResourceCenter = () => {
    // Consultants have a dedicated resource center URL (TCX-530). Other
    // roles fall back to the default. Both values come from runtime config
    // via window.__APP_CONFIG__ / /config.js.
    const url =
      user?.role === UserRole.CONSULTANT
        ? config.VITE_CONSULTANT_RESOURCE_CENTER_URL ||
          config.VITE_RESOURCE_CENTER_URL
        : config.VITE_RESOURCE_CENTER_URL;

    if (!url) {
      console.error('Resource Center URL is not configured');
      return;
    }

    openExternal(url);
    setProfileVisible(false);
  };

  const handlePremiumDashboard = () => {
    const url = user?.premiumDashboardUrl;
    if (!url) return;
    // Admin-entered URL — open in a new tab with opener hardening.
    window.open(url, '_blank', 'noopener,noreferrer');
    setProfileVisible(false);
  };

  const menuOptions = [
    // TCX-737: franchisors with a saved TCX Premium Dashboard URL get a menu
    // entry above Resource Center that opens it (mobile drawer has no Settings).
    ...(user?.role === UserRole.FRANCHISEE && user?.premiumDashboardUrl
      ? [
          {
            icon: <SvgIcon type="premiumDashboard" />,
            label: 'TCX Premium Dashboard',
            onClick: handlePremiumDashboard,
          },
        ]
      : []),
    {
      icon: <SvgIcon type="book" />,
      label: 'Resource Center',
      onClick: handleResourceCenter,
    },
    {
      icon: <SvgIcon type="help" />,
      label: 'Support',
      onClick: handleSupport,
    },
    {
      icon: <SvgIcon type="logoutDark" />,
      label: 'Log Out',
      onClick: handleLogout,
    },
  ];

  // Drop external nav items whose (runtime-config) target isn't a safe http(s)
  // URL, so a non-http(s) scheme can never reach a rendered href. Internal
  // (react-router) items always pass through. TCX-797.
  const safeNavigation = navigation.filter(
    ({ external, path }) => !external || isSafeExternalUrl(path),
  );

  const menu: MenuProps['items'] = safeNavigation.map(
    ({ path, label, icon, external }) => ({
      key: path,
      label: external ? (
        <a
          href={path}
          target="_blank"
          rel="noopener noreferrer"
          onClick={onClose}
          className="link"
        >
          <Flex
            align={'center'}
            gap={20}
            style={{ '--icon-color': '#49586F' } as React.CSSProperties}
          >
            <SvgIcon type={icon as IconsType} />
            <Typography.Text>{label}</Typography.Text>
          </Flex>
        </a>
      ) : (
        <NavLink
          to={path}
          onClick={onClose}
          className={({ isActive }) => (isActive ? 'active link' : 'link')}
        >
          {({ isActive }) => (
            <Flex
              align={'center'}
              gap={20}
              style={
                {
                  '--icon-color': isActive ? '#2A4BC2' : '#49586F',
                } as React.CSSProperties
              }
            >
              <SvgIcon type={icon as IconsType} />
              <Typography.Text>{label}</Typography.Text>
            </Flex>
          )}
        </NavLink>
      ),
    }),
  );
  return (
    <Fragment>
      {lg ? (
        <div className={'navigation'}>
          <nav className={'navigation-list'}>
            {safeNavigation.map(({ path, label, external }) =>
              external ? (
                <a
                  href={path}
                  key={path}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="link"
                  style={{ position: 'relative' }}
                >
                  {label}
                </a>
              ) : (
                <NavLink
                  to={path}
                  key={path}
                  className={({ isActive }) =>
                    isActive ? 'active link' : 'link'
                  }
                  style={{ position: 'relative' }}
                >
                  {label}
                  {label === 'Messages' && unreadCount > 0 && (
                    <span className="unread-count">{unreadCount}</span>
                  )}
                </NavLink>
              ),
            )}
          </nav>
        </div>
      ) : (
        <Drawer
          open={open}
          onClose={onClose}
          width={'70%'}
          placement={'left'}
          closable={false}
          className={'navigation-drawer'}
          title={
            <Flex align={'center'} gap={16}>
              <Logo />

              <CloseOutlined style={{ marginLeft: 'auto' }} onClick={onClose} />
            </Flex>
          }
        >
          <Flex vertical justify="space-between" style={{ height: '100%' }}>
            <Menu items={menu} />

            {/* Bottom menu item - not in the Menu component */}
            <Flex vertical gap={16} style={{ paddingBlock: '16px' }}>
              {isProfileVisible && isAuthenticated && (
                <div className="mobile-profile-container">
                  <div className="profile-header">
                    <div className="profile-info">
                      <Text className="business-name">
                        {user?.role === UserRole.CONSULTANT
                          ? `${user?.firstName} ${user?.lastName}`
                          : user?.company || '-'}
                      </Text>
                      <Text className="user-name">
                        {user?.role === UserRole.CONSULTANT
                          ? // TCX-507: groups may be {id,name} refs or name strings
                            user?.groups
                              ?.map((g: any) =>
                                typeof g === 'string' ? g : g?.name,
                              )
                              .join(', ') || '-'
                          : user?.categories?.join(', ') || '-'}
                      </Text>
                    </div>
                  </div>

                  <Flex vertical gap={8} style={{ paddingBlock: '16px' }}>
                    <div className="edit-profile-section">
                      <CustomButton
                        type="primary"
                        onClick={handleEditProfile}
                        text="EDIT PROFILE"
                      ></CustomButton>
                    </div>

                    <div className="edit-profile-section">
                      <CustomButton
                        type="default"
                        onClick={() => {
                          if (user?.role === UserRole.CONSULTANT) {
                            window.open(
                              ROUTES.FIND_CONSULTANT + '/' + user.id,
                              '_blank',
                            );
                          } else {
                            window.open(
                              ROUTES.FIND_FRANCHISORS + '/' + userId,
                              '_blank',
                            );
                          }
                          // onClose();
                        }}
                        text="PREVIEW PROFILE"
                      ></CustomButton>
                    </div>
                  </Flex>

                  <div className="profile-menu">
                    {menuOptions.map((option, index) => (
                      <div
                        key={index}
                        className="menu-item"
                        onClick={option.onClick}
                      >
                        <span className="menu-icon">{option.icon}</span>
                        <Text>{option.label}</Text>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div style={{ display: 'flex', gap: 16 }}>
                <Button
                  type="link"
                  icon={<SvgIcon type={'user'} fill="#49586F" />}
                  onClick={() => setProfileVisible(!isProfileVisible)}
                >
                  <span
                    style={{ fontSize: 16, color: '#49586F', fontWeight: 600 }}
                  >
                    My Profile
                  </span>
                </Button>
              </div>
            </Flex>
          </Flex>
        </Drawer>
      )}
    </Fragment>
  );
};
